import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Key, Plus, Trash2, Eye, EyeOff, Copy, RotateCw, RefreshCw, Check } from 'lucide-react';

const ACCENT = '#f97316';

interface ApiKeyEntry {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  request_count: number;
  rate_limit_per_minute: number;
  rotation_status: string | null;
  grace_expires_at: string | null;
}

const AVAILABLE_SCOPES = [
  '*', 'tickets:read', 'tickets:write', 'tickets:*',
  'contacts:read', 'contacts:write', 'contacts:*',
  'companies:read', 'companies:write', 'companies:*',
  'invoices:read', 'invoices:write', 'invoices:*',
  'devices:read', 'alerts:read', 'jobs:read', 'jobs:write',
];

export default function APIKeysPage() {
  const [keys, setKeys] = useState<ApiKeyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  // Create form state
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['*']);
  const [rateLimit, setRateLimit] = useState(60);
  const [expiresAt, setExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const data = await api.get<{ data: ApiKeyEntry[]; total: number }>('/gateway/keys');
      setKeys(data.data);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const result = await api.post<{ id: string; raw_key: string }>('/gateway/keys', {
        name: name.trim(),
        scopes,
        rate_limit_per_minute: rateLimit,
        ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
      });
      setNewKeyValue(result.raw_key);
      setShowCreate(false);
      setName('');
      setScopes(['*']);
      setRateLimit(60);
      setExpiresAt('');
      loadKeys();
    } catch (err: any) {
      alert(err.message || 'Failed to create key');
    }
    setCreating(false);
  };

  const handleRevoke = async (id: string) => {
    try {
      await api.delete(`/gateway/keys/${id}`);
      setConfirmRevoke(null);
      loadKeys();
    } catch { /* empty */ }
  };

  const handleRotate = async (id: string) => {
    try {
      const result = await api.post<{ raw_key: string }>(`/gateway/keys/${id}`, {
        action: 'rotate',
        grace_period_hours: 24,
      });
      setNewKeyValue(result.raw_key);
      loadKeys();
    } catch (err: any) {
      alert(err.message || 'Failed to rotate key');
    }
  };

  const handleCopy = () => {
    if (newKeyValue) {
      navigator.clipboard.writeText(newKeyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading API keys...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>API Keys</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Manage org-level API keys for the Bridge Gateway</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnStyle}>
          <Plus size={16} /> Create Key
        </button>
      </div>

      {/* New Key Reveal Modal */}
      {newKeyValue && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 0.5rem' }}>API Key Created</h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1rem' }}>
              Copy this key now — it won't be shown again.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                readOnly
                value={showKey ? newKeyValue : '\u2022'.repeat(40)}
                style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.75rem', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', backgroundColor: '#f9fafb' }}
              />
              <button onClick={() => setShowKey(!showKey)} style={iconBtnStyle}>
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={handleCopy} style={iconBtnStyle}>
                {copied ? <Check size={16} color="#16a34a" /> : <Copy size={16} />}
              </button>
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => { setNewKeyValue(null); setShowKey(false); }} style={btnStyle}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Key Modal */}
      {showCreate && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>Create API Key</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Production API Key" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Scopes</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {AVAILABLE_SCOPES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        borderRadius: '9999px',
                        border: '1px solid',
                        borderColor: scopes.includes(s) ? ACCENT : '#d1d5db',
                        backgroundColor: scopes.includes(s) ? ACCENT : '#fff',
                        color: scopes.includes(s) ? '#fff' : '#374151',
                        cursor: 'pointer',
                      }}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Rate Limit (requests/min)</label>
                <input type="number" value={rateLimit} onChange={(e) => setRateLimit(Number(e.target.value))} min={1} max={10000} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Expires At (optional)</label>
                <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={{ ...btnStyle, backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Cancel</button>
              <button onClick={handleCreate} disabled={creating || !name.trim() || scopes.length === 0} style={{ ...btnStyle, opacity: creating ? 0.6 : 1 }}>
                {creating ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Key List */}
      {keys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <Key size={32} color="#9ca3af" style={{ margin: '0 auto 0.75rem' }} />
          <p style={{ color: '#6b7280', margin: '0 0 1rem' }}>No API keys created yet</p>
          <button onClick={() => setShowCreate(true)} style={btnStyle}>Create API Key</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {keys.map((k) => (
            <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
              <Key size={16} color="#9ca3af" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{k.name}</span>
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    fontSize: '0.6875rem',
                    borderRadius: '9999px',
                    backgroundColor: k.is_active ? '#dcfce7' : '#f3f4f6',
                    color: k.is_active ? '#166534' : '#6b7280',
                    fontWeight: 500,
                  }}>
                    {k.is_active ? (k.rotation_status === 'rotating' ? 'Rotating' : 'Active') : 'Revoked'}
                  </span>
                </div>
                <p style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#9ca3af', margin: '0.125rem 0' }}>brk_{k.key_prefix}...</p>
                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                  {k.scopes.map((s) => (
                    <span key={s} style={{ padding: '0.0625rem 0.375rem', fontSize: '0.6875rem', borderRadius: '0.25rem', border: '1px solid #e5e7eb', color: '#6b7280' }}>{s}</span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {k.request_count > 0 && <p style={{ margin: 0 }}>{k.request_count.toLocaleString()} requests</p>}
                {k.last_used_at && <p style={{ margin: 0 }}>Last: {new Date(k.last_used_at).toLocaleDateString()}</p>}
                {k.expires_at && <p style={{ margin: 0 }}>Exp: {new Date(k.expires_at).toLocaleDateString()}</p>}
                {k.grace_expires_at && <p style={{ margin: 0, color: ACCENT }}>Grace: {new Date(k.grace_expires_at).toLocaleString()}</p>}
              </div>
              {k.is_active && (
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button onClick={() => handleRotate(k.id)} title="Rotate (24h grace)" style={iconBtnStyle}>
                    <RotateCw size={14} />
                  </button>
                  {confirmRevoke === k.id ? (
                    <>
                      <button onClick={() => handleRevoke(k.id)} style={{ ...iconBtnStyle, color: '#dc2626' }} title="Confirm revoke">
                        <Check size={14} />
                      </button>
                      <button onClick={() => setConfirmRevoke(null)} style={iconBtnStyle} title="Cancel">
                        <RefreshCw size={14} />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmRevoke(k.id)} style={iconBtnStyle} title="Revoke">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const btnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
  padding: '0.5rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 500,
  backgroundColor: ACCENT,
  color: '#fff',
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer',
};

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2rem',
  height: '2rem',
  backgroundColor: 'transparent',
  border: '1px solid #e5e7eb',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  color: '#6b7280',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: '0.75rem',
  padding: '1.5rem',
  width: '100%',
  maxWidth: '480px',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  fontSize: '0.875rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  boxSizing: 'border-box',
};
